import React, { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { round } from 'lodash-es';

interface LipscoreState {
  ratings: { [id: string]: any };
  addRating: (id: string, rating: any) => void;
}

const useLipscoreState = create<LipscoreState>((set) => ({
  ratings: {},
  addRating: (id, rating) =>
    set((state) => ({ ratings: { ...state.ratings, [id]: rating } })),
}));

const fetchRating = (id) => {
  if (!globalThis.lipscoreApiKey) {
    return new Promise((_, reject) => reject('No Lipscore API key found'));
  }
  const url = `https://wapi.lipscore.com/initial_data/products/show?api_key=${globalThis.lipscoreApiKey}&internal_id=${id}&widgets=r,r_sm,rw_l&translate_to_lang=${globalThis.lipscoreLocale}`;
  return fetch(url).then((res) => res.json());
};

const Star = ({ starState }) => (
  <svg
    viewBox="0 0 19 18.1"
    className={`lipscore-rating-star lipscore-svg-rating-star ${starState
      ? `lipscore-star-${starState} lipscore-rating-star-${starState}`
      : ''
      }`}
    width="16px"
    height="16px"
    style={{ fillRule: 'evenodd' }}
  >
    <g transform="matrix(0.896868,0,0,0.983811,-0.279534,0)">
      <path d="M11.573,1.193L11.573,15.61L10.916,15.3L4.362,18.388L5.558,11.752L0.312,7.024L7.605,6.01L10.916,0L11.573,1.193Z"></path>
    </g>
    <g
      className="lipscore-star-last-half"
      transform="matrix(1,0,0,1,-133.489,-464)"
    >
      <path d="M142.989,464.021L143,464L145.97,469.912L152.511,470.91L147.805,475.561L148.878,482.09L143,479.052L142.989,479.058L142.989,464.021Z"></path>
    </g>
  </svg>
);

const ProductCardLipscoreStars = ({ productModel, showReviewsCount, className = '' }) => {
  const { ratings, addRating } = useLipscoreState();
  const rating = useMemo(() => {
    const ratingValue = ratings[productModel.productId]?.lipscore;
    return typeof ratingValue !== 'undefined'
      ? round(parseFloat(ratingValue) / 2, 1)
      : undefined;
  }, [ratings, productModel]);

  useEffect(() => {
    if (!ratings[productModel.productId]) {
      fetchRating(productModel.productId)
        .then((rating) => addRating(productModel.productId, rating))
        .catch((err) => console.log(err));
    }
  }, []);

  const getStarState = (index: number) => {
    if (typeof rating === 'undefined') return undefined;

    const diff = rating - index;
    if (diff > 0 && diff < 1) {
      return 'incomplete';
    } else if (diff <= 0) {
      return 'inactive';
    }
    return undefined;
  };

  return rating ? (
    <div className={`flex gap-1 ${className}`}>
      <div className="mb-1">
        {Array.from({ length: 5 }).map((_, idx) => (
          <Star
            starState={getStarState(idx)}
            key={`lipscore-stars-${productModel.productId}-${idx}`}
          />
        ))}
      </div>
      {showReviewsCount && (
        <div className='self-center'>
          {`(${ratings[productModel.productId]?.votes})`}
        </div>
      )}
    </div>
  ) : null;
};

export default ProductCardLipscoreStars;
